/**
 * Exxy replies come back as light markdown: `[label](/path)` links, `**bold**`, `` `code` ``, backtick
 * paths like `/portal/team`, and the occasional URL. The app has no markdown dependency, and Exxy's
 * output is simple, so this renders just that subset into React nodes — and, crucially, turns the
 * links into real anchors that navigate within the SPA (the point of request #2: words that link, not
 * literal asterisks or bare paths). Anything unrecognised is shown as plain text.
 */
import { useNavigate } from '@tanstack/react-router';
import { Fragment, type ReactNode } from 'react';

const FORMAT = /(\*\*[^*]+\*\*|`[^`]+`)/g;
const LINK = /(https?:\/\/[^\s)]+|\/[A-Za-z0-9][A-Za-z0-9/_-]*)/g;
// A markdown link: [visible words](/portal/path) or [docs](https://…). Highest precedence.
const MD_LINK = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+|\/[A-Za-z0-9][A-Za-z0-9/_-]*)\)/g;

type Go = (to: string) => void;

function Anchor({ href, go, className, children }: { href: string; go: Go; className: string; children: ReactNode }) {
  const internal = href.startsWith('/');
  return (
    <a
      href={href}
      {...(internal ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
      onClick={(e) => {
        if (internal) {
          e.preventDefault();
          go(href);
        }
      }}
      className={className}
    >
      {children}
    </a>
  );
}

const BARE_LINK_CLASS = 'font-medium text-[var(--oex-purple)] underline underline-offset-2 hover:opacity-80';

function linkify(text: string, go: Go, keyBase: string): ReactNode[] {
  return text.split(LINK).map((part, i) => {
    const key = `${keyBase}-${i}`;
    if (i % 2 === 1) {
      return (
        <Anchor key={key} href={part} go={go} className={BARE_LINK_CLASS}>
          {part}
        </Anchor>
      );
    }
    return <Fragment key={key}>{part}</Fragment>;
  });
}

/** Render `**bold**`, `` `code` ``, backtick paths and bare URLs within a markdown-link-free segment. */
function renderInline(text: string, go: Go, keyBase: string): ReactNode[] {
  return text.split(FORMAT).map((part, i) => {
    const key = `${keyBase}-f${i}`;
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={key}>{linkify(part.slice(2, -2), go, key)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      const inner = part.slice(1, -1);
      if (inner.startsWith('/')) {
        return (
          <Anchor key={key} href={inner} go={go} className="font-data rounded bg-muted px-1 py-0.5 text-[0.85em] text-[var(--oex-purple)] underline underline-offset-2 hover:opacity-80">
            {inner}
          </Anchor>
        );
      }
      return (
        <code key={key} className="font-data rounded bg-muted px-1 py-0.5 text-[0.85em]">
          {inner}
        </code>
      );
    }
    return <Fragment key={key}>{linkify(part, go, key)}</Fragment>;
  });
}

export function ExxyText({ text }: { text: string }) {
  const navigate = useNavigate();
  const go: Go = (to) => void (navigate as (opts: { to: string }) => Promise<void>)({ to });
  // Split on markdown links first: with two capture groups, split yields [text, label, target, text, …].
  const chunks = text.split(MD_LINK);
  return (
    <>
      {chunks.map((chunk, i) => {
        const key = `p-${i}`;
        const slot = i % 3;
        if (slot === 1) return null; // label — consumed with its target below
        if (slot === 2) {
          const label = chunks[i - 1] ?? '';
          return (
            <Anchor key={key} href={chunk} go={go} className={BARE_LINK_CLASS}>
              {label}
            </Anchor>
          );
        }
        return <Fragment key={key}>{renderInline(chunk, go, key)}</Fragment>;
      })}
    </>
  );
}
